import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { Plug, Pencil, MoreHorizontal, Search, Trash2, Activity, PackageX, Undo2, RefreshCw, Plus } from "lucide-react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { agentOutdated, api, copyOrWarn, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GroupColumn } from "@/components/GroupColumn";
import { MachineRow } from "@/components/MachineRow";
import { DetailPanel } from "@/components/DetailPanel";
import { EditMachineDialog } from "@/components/EditMachineDialog";
import { MachineMenu } from "@/components/machine/MachineMenu";
import { CommandDialog, MessageDialog, NoteDialog } from "@/components/machine/QuickDialogs";
import { ToolboxDialog } from "@/components/machine/ToolboxDialog";
import { GenerateDialog } from "@/components/GenerateDialog";
import { AccessSide2 } from "@/components/sv2/AccessSide2";
import { MachineList2, Row2, sortMachines } from "@/components/sv2/MachineList2";
import { DetailCard2 } from "@/components/sv2/DetailCard2";

const IconBtn = ({ icon: Icon, title, onClick, disabled, id, ...rest }) => (
  <button data-testid={id} title={title} onClick={onClick} disabled={disabled} {...rest}
    className="h-7 w-7 rounded-md flex items-center justify-center text-slate-600 hover:bg-slate-100 hover:text-sky-700 disabled:text-slate-300 disabled:hover:bg-transparent"><Icon className="h-4 w-4" /></button>
);

const ToolBtn = ({ icon: Icon, label, onClick, disabled, id }) => (
  <button data-testid={id} onClick={onClick} disabled={disabled} className="flex items-center gap-1.5 text-[17px] text-slate-800 hover:text-blue-600 disabled:text-slate-300 disabled:cursor-not-allowed">
    <Icon className="h-5 w-5" strokeWidth={1.6} /> {label}
  </button>
);

const FILTERS = {
  all: () => true,
  sessions: (m) => m.in_session,
  online: (m) => m.online,
  none: (m) => !m.group_id,
};

export default function Access() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { billing, help, layout, sideSlot, mini } = useOutletContext();
  const sv2 = layout === "sv2";
  const rmm = !billing?.modules || billing.modules.includes("rmm");
  const [tab, setTab] = useState("info");
  const [sort, setSort] = useState("name");
  const [params, setParams] = useSearchParams();
  const [machines, setMachines] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [groups, setGroups] = useState([]);
  const [selGroup, setSelGroup] = useState(params.get("help") ? "help" : "all");
  useEffect(() => { if (params.get("help")) { setSelGroup("help"); setParams({}, { replace: true }); } }, [params, setParams]);
  const helpBy = useMemo(() => help.open.reduce((acc, r) => ({ ...acc, [r.machine_id]: [...(acc[r.machine_id] || []), r] }), {}), [help.open]);
  const helpResolved = () => { help.reload(); if (selGroup === "help") setSelId(null); };
  const [selId, setSelId] = useState(null);
  const [checked, setChecked] = useState(() => new Set());
  const pick = (id) => { setSelId(id); setChecked(new Set(id ? [id] : [])); };
  const toggleCheck = (m) => setChecked((cur) => {
    const next = new Set(cur);
    if (next.has(m.machine_id)) next.delete(m.machine_id); else next.add(m.machine_id);
    if (!next.has(selId)) setSelId(next.size ? [...next][next.size - 1] : null);
    return next;
  });
  const [q, setQ] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [dlg, setDlg] = useState({ kind: null, m: null });
  const closeDlg = () => setDlg({ kind: null, m: null });
  const canConnect = billing?.state !== "expired";

  const load = useCallback(async () => {
    const [m, g] = await Promise.all([api.get("/machines"), api.get("/groups")]);
    setMachines(m.data);
    setGroups(g.data);
    setLoaded(true);
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  const groupMap = useMemo(() => Object.fromEntries(groups.map((g) => [g.group_id, g])), [groups]);
  const [latest, setLatest] = useState(null);
  useEffect(() => { api.get("/agents/latest").then(({ data }) => setLatest(data)).catch(() => {}); }, []);
  const isOld = useCallback((m) => !!m.agent_version && agentOutdated(m.agent_version, latest?.latest), [latest]);
  const filter = selGroup === "help" ? (m) => !!helpBy[m.machine_id] : selGroup === "outdated" ? isOld : FILTERS[selGroup] || ((m) => m.group_id === selGroup);
  const updateAgents = async (ids) => {
    if (!latest?.published_ok) { toast.error("La version " + (latest?.latest || "") + " n'est pas encore compilée sur GitHub : relancez la compilation."); return; }
    if (!window.confirm(`Mettre à jour l'agent vers la version ${latest.latest} sur ${ids.length} poste(s) ? L'agent redémarre tout seul (moins d'une minute de coupure).`)) return;
    try {
      const { data } = await api.post("/agents/update", { machine_ids: ids });
      if (data.sent.length) toast.success(`Mise à jour envoyée à ${data.sent.length} poste(s)`);
      if (data.skipped.length) toast.warning(data.skipped.map((s) => `${s.name} : ${s.reason}`).join(" · "));
      setTimeout(load, 60000);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const list = machines.filter(filter).filter((m) => !q || `${m.hostname} ${m.display_name || ""} ${m.username} ${m.ip_local} ${m.ip_public}`.toLowerCase().includes(q.toLowerCase()));
  const selected = machines.find((m) => m.machine_id === selId);
  const allChecked = list.length > 0 && list.every((m) => checked.has(m.machine_id));
  const toggleAll = () => {
    if (allChecked) { setChecked(new Set()); setSelId(null); } else { setChecked(new Set(list.map((m) => m.machine_id))); if (!selId && list[0]) setSelId(list[0].machine_id); }
  };
  const checkedMachines = machines.filter((m) => checked.has(m.machine_id));
  const checkedOld = checkedMachines.filter(isOld);
  const title = { all: "Toutes les machines", help: "Demandes d'aide", outdated: "Agents à mettre à jour", sessions: "Sessions en cours", online: "En ligne", none: "Non classé" }[selGroup] || groupMap[selGroup]?.name || "";

  const connect = (m) => {
    if (!canConnect) return toast.error("Essai terminé : abonnez-vous pour vous connecter.");
    if (!m.online) return toast.error("Machine hors ligne");
    window.open(`/session/${m.machine_id}`, "_blank");
  };
  const uninstall = async (target) => {
    const selected = target?.machine_id ? target : machines.find((x) => x.machine_id === selId);
    const name = selected.display_name || selected.hostname;
    if (!window.confirm(`Désinstaller l'agent de ${name} ?\n\nLe logiciel sera retiré du poste et la machine disparaîtra de la liste. ${selected.online ? "" : "Le poste est hors ligne : ce sera fait dès sa reconnexion."}`)) return;
    try {
      const { data } = await api.post(`/machines/${selected.machine_id}/uninstall`);
      toast.success(data.online ? `Désinstallation lancée sur ${name}` : `${name} sera désinstallé dès sa reconnexion`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const cancelUninstall = (target) => api.post(`/machines/${(target?.machine_id ? target : selected).machine_id}/uninstall/cancel`)
    .then(() => { toast.success("Désinstallation annulée"); load(); }).catch((e) => toast.error(errMsg(e)));
  const remove = async (target) => {
    const selected = target?.machine_id ? target : machines.find((x) => x.machine_id === selId);
    if (!window.confirm(`Retirer ${selected.hostname} de la liste ? Le logiciel n'est pas désinstallé du poste mais il ne pourra plus se connecter. Pour l'enlever du PC, utilisez « Désinstaller l'agent du poste ».`)) return;
    try {
      await api.delete(`/machines/${selected.machine_id}`);
      setSelId(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const wake = (m) => api.post(`/machines/${m.machine_id}/wake`)
    .then(({ data }) => toast.success(`Signal de réveil envoyé par ${data.relays.join(", ")}. Le poste devrait apparaître en ligne d'ici 1 à 2 minutes.`))
    .catch((e) => toast.error(errMsg(e)));
  const restart = (m) => {
    const name = m.display_name || m.hostname;
    if (!window.confirm(`Redémarrer ${name} ? L'utilisateur est prévenu et le poste redémarre dans 1 minute.`)) return;
    const mac = m.os && m.os !== "Windows";
    api.post(`/machines/${m.machine_id}/command`, mac ? { command: "shutdown -r +1", shell: "auto" } : { command: 'shutdown /r /t 60 /c "Redemarrage demande par votre technicien"', shell: "cmd" }, { timeout: 140000 })
      .then(() => toast.success(`Redémarrage de ${name} programmé dans 1 minute`))
      .catch((e) => toast.error(errMsg(e)));
  };
  const act = {
    select: (m) => { if (!checked.has(m.machine_id)) pick(m.machine_id); else setSelId(m.machine_id); }, connect, wake, restart, uninstall, cancelUninstall, remove,
    update: (m) => updateAgents([m.machine_id]),
    tab: (m, t) => { pick(m.machine_id); setTab(t); },
    open: (kind, m) => { act.select(m); if (kind === "edit") setEditOpen(true); else setDlg({ kind, m }); },
  };

  const moreItems = (
    <DropdownMenuContent>
      {user?.role === "admin" && selected && (selected.uninstall_requested_at
        ? <DropdownMenuItem onClick={() => cancelUninstall()} data-testid="toolbar-uninstall-cancel-btn"><Undo2 className="h-4 w-4 mr-2" /> Annuler la désinstallation</DropdownMenuItem>
        : <DropdownMenuItem onClick={() => uninstall()} data-testid="toolbar-uninstall-btn" className="text-red-600"><PackageX className="h-4 w-4 mr-2" /> Désinstaller l'agent du poste</DropdownMenuItem>)}
      {user?.role === "admin" && selected && <DropdownMenuItem onClick={() => remove()} data-testid="toolbar-delete-btn" className="text-red-600"><Trash2 className="h-4 w-4 mr-2" /> Retirer de la liste seulement</DropdownMenuItem>}
      {user?.role === "admin" && selected && isOld(selected) && <DropdownMenuItem onClick={() => updateAgents([selected.machine_id])} data-testid="toolbar-agent-update-btn"><RefreshCw className="h-4 w-4 mr-2" /> Mettre à jour l'agent ({latest?.latest})</DropdownMenuItem>}
      {user?.role === "admin" && machines.some(isOld) && <DropdownMenuItem onClick={() => updateAgents(machines.filter(isOld).map((m) => m.machine_id))} data-testid="toolbar-agent-update-all-btn"><RefreshCw className="h-4 w-4 mr-2" /> Mettre à jour tous les agents en retard ({machines.filter(isOld).length})</DropdownMenuItem>}
      <DropdownMenuItem onClick={() => selected && copyOrWarn(selected.machine_id, "Identifiant copié")}>Copier l'identifiant</DropdownMenuItem>
    </DropdownMenuContent>
  );
  const bulkBar = checked.size > 1 && (
    <div className="flex items-center gap-4 px-5 py-2.5 bg-blue-50 border-b border-blue-100 text-sm" data-testid="bulk-bar">
      <span className="font-semibold" data-testid="bulk-count">{checked.size} machine(s) sélectionnée(s)</span>
      {user?.role === "admin" && (
        <button disabled={!checkedOld.length} onClick={() => updateAgents(checkedOld.map((m) => m.machine_id))} data-testid="bulk-update-btn"
          className="flex items-center gap-1.5 font-medium text-blue-700 hover:text-blue-900 disabled:text-slate-400">
          <RefreshCw className="h-4 w-4" /> Mettre à jour l'agent ({checkedOld.length} en retard)
        </button>
      )}
      <button onClick={() => pick(null)} className="ml-auto text-slate-600 hover:text-slate-900" data-testid="bulk-clear-btn">Tout désélectionner</button>
    </div>
  );
  const rowSelect = (x) => pick(selId === x.machine_id && checked.size <= 1 ? null : x.machine_id);
  const empty = !list.length && (
    <p className="p-10 text-center text-slate-500" data-testid="machines-empty">
      {machines.length ? "Aucune machine dans cette vue." : sv2 ? "Aucun poste pour l'instant." : "Aucune machine : cliquez sur « Générer + » pour installer l'agent chez un client."}
    </p>
  );
  const dialogs = (
    <>
      <EditMachineDialog machine={selected} groups={groups} open={editOpen} onOpenChange={setEditOpen} onSaved={load} />
      <MessageDialog m={dlg.kind === "message" ? dlg.m : null} onClose={closeDlg} />
      <CommandDialog m={dlg.kind === "command" ? dlg.m : null} onClose={closeDlg} />
      <NoteDialog m={dlg.kind === "note" ? dlg.m : null} onClose={closeDlg} onSaved={load} />
      <ToolboxDialog m={dlg.kind === "toolbox" ? dlg.m : null} onClose={closeDlg} />
    </>
  );

  if (sv2) return (
    <>
      {sideSlot && createPortal(<AccessSide2 mini={mini} machines={machines} groups={groups} selected={selGroup} onSelect={setSelGroup} onChanged={load} helpCount={Object.keys(helpBy).length} outdatedCount={machines.filter(isOld).length} />, sideSlot)}
      <MachineList2 title={title} count={list.length} q={q} setQ={setQ} sort={sort} setSort={setSort} allChecked={allChecked} someChecked={list.some((m) => checked.has(m.machine_id))} toggleAll={toggleAll} bulk={bulkBar}
        toolbar={<>
          <GenerateDialog trigger={<button data-testid="generate-installer-btn" title="Générer un programme d'installation" className="h-7 w-7 rounded-md bg-sky-500 hover:bg-sky-600 text-white flex items-center justify-center"><Plus className="h-4 w-4" /></button>} />
          <IconBtn id="toolbar-edit-btn" icon={Pencil} title="Modifier" disabled={!selected} onClick={() => setEditOpen(true)} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild><IconBtn id="toolbar-more-btn" icon={MoreHorizontal} title="Plus" disabled={!selected && !machines.some(isOld)} /></DropdownMenuTrigger>
            {moreItems}
          </DropdownMenu>
        </>}>
        {sortMachines(list, sort, groupMap).map((m) => (
          <MachineMenu key={m.machine_id} m={m} admin={user?.role === "admin"} outdated={isOld(m)} latest={latest?.latest} canConnect={canConnect} act={act}>
            <Row2 m={m} group={groupMap[m.group_id]} help={helpBy[m.machine_id]} outdated={!m.agent_version || isOld(m)} selected={m.machine_id === selId} checked={checked.has(m.machine_id)} onCheck={toggleCheck} onSelect={rowSelect} onOpen={connect} act={act} />
          </MachineMenu>
        ))}
        {empty}
      </MachineList2>
      <DetailCard2 m={selected} latest={latest?.latest} help={selected && helpBy[selected.machine_id]} onHelpResolved={helpResolved} group={groupMap[selected?.group_id]} onConnect={connect} onSaved={load} canConnect={canConnect} act={act} tab={tab} setTab={setTab} rmm={rmm}
        welcome={loaded && !machines.length && <GenerateDialog trigger={<button data-testid="welcome-install-btn" className="h-11 rounded-md bg-sky-500 hover:bg-sky-600 text-white font-semibold px-6 inline-flex items-center gap-2"><Plus className="h-5 w-5" />Installer mon premier poste</button>} />}
        stats={{ online: machines.filter((x) => x.online).length, offline: machines.filter((x) => !x.online).length, outdated: machines.filter(isOld).length, help: Object.keys(helpBy).length }} />
      {dialogs}
    </>
  );

  return (
    <>
      <GroupColumn machines={machines} groups={groups} selected={selGroup} onSelect={setSelGroup} onChanged={load} helpCount={Object.keys(helpBy).length} outdatedCount={machines.filter(isOld).length} />
      <section className="flex-1 min-w-[460px] border-r border-slate-200 flex flex-col min-h-0" data-testid="machine-list">
        <div className="h-[72px] flex items-center gap-6 px-5 border-b border-slate-200">
          <h2 className="text-[22px] truncate max-w-[340px] uppercase" data-testid="list-title">{title}</h2>
          <ToolBtn id="toolbar-connect-btn" icon={Plug} label="Participer" disabled={!selected?.online || !canConnect} onClick={() => connect(selected)} />
          {(!billing?.modules || billing.modules.includes("rmm")) && <ToolBtn id="toolbar-rmm-btn" icon={Activity} label="RMM" disabled={!selected} onClick={() => navigate(`/app/rmm/${selected.machine_id}`)} />}
          <ToolBtn id="toolbar-edit-btn" icon={Pencil} label="Modifier" disabled={!selected} onClick={() => setEditOpen(true)} />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="toolbar-more-btn" disabled={!selected && !machines.some(isOld)} className="flex items-center gap-1.5 text-[17px] text-slate-800 hover:text-blue-600 disabled:text-slate-300"><MoreHorizontal className="h-5 w-5" /> Plus</button>
            </DropdownMenuTrigger>
            {moreItems}
          </DropdownMenu>
        </div>
        <div className="flex items-center gap-4 px-5 py-4 border-b border-slate-200">
          <input type="checkbox" checked={allChecked} disabled={!list.length} onChange={toggleAll} title="Tout sélectionner" className="h-5 w-5 accent-blue-600 cursor-pointer" data-testid="machines-check-all"
            ref={(el) => { if (el) el.indeterminate = !allChecked && list.some((m) => checked.has(m.machine_id)); }} />
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
            <input data-testid="search-machine-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Rechercher ${title}`} className="w-full h-12 rounded-full border border-slate-300 pl-12 pr-4 text-[16px] outline-none focus:border-blue-500" />
          </div>
        </div>
        {bulkBar}
        <div className="flex-1 overflow-y-auto">
          {list.map((m) => (
            <MachineMenu key={m.machine_id} m={m} admin={user?.role === "admin"} outdated={isOld(m)} latest={latest?.latest} canConnect={canConnect} act={act}>
              <MachineRow m={m} latest={latest?.latest} help={helpBy[m.machine_id]} group={groupMap[m.group_id]} selected={m.machine_id === selId} checked={checked.has(m.machine_id)} onCheck={toggleCheck} onSelect={rowSelect} onOpen={connect} />
            </MachineMenu>
          ))}
          {empty}
        </div>
      </section>
      <DetailPanel m={selected} latest={latest?.latest} help={selected && helpBy[selected.machine_id]} onHelpResolved={helpResolved} group={groupMap[selected?.group_id]} onConnect={connect} onSaved={load} canConnect={canConnect} />
      {dialogs}
    </>
  );
}
